import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Checkbox,
  Collapse,
  Group,
  MultiSelect,
  NativeSelect,
  Popover,
  Loader,
  Stack as StackMenu,
  Text,
  TextInput,
} from '@mantine/core'
import type { SetURLSearchParams } from 'react-router-dom'
import {
  IconEdit,
  IconFilter,
  IconLayoutColumns,
  IconMessage,
  IconRefresh,
  IconRobot,
  IconRotateClockwise,
  IconSearch,
  IconX,
} from '@/components/icons'
import { ActionButton } from '@/components/ui'
import { ShiftPresenceDot } from '@/components/ui/ShiftPresenceDot'
import {
  TICKETS_PAGE_SIZE_OPTIONS,
  isTicketsPageSizeOption,
  type TicketsPageSizeOption,
} from '@/lib/tickets-list-page-size'
import { stripTicketsListPageParam } from '@/lib/tickets-list-page-param'
import type { TicketsSortByKey } from '@/lib/tickets-list-sort'
import {
  canonicalPriorityQueryValue,
  canonicalStatusQueryValue,
  excludeResolvedClosedDraftFromParams,
} from '@/lib/tickets-list-filters-url'
import {
  TICKETS_LIST_COLUMNS_MENU_ORDER,
  TICKETS_LIST_COLUMN_LABEL,
  type TicketsListColumnId,
  type TicketsListColumnVisibility,
} from '@/lib/tickets-list-columns'
import { canAssignTicketAsWorkspaceUser } from '@/lib/workspace-user-roles'
import {
  TICKETS_LIST_PERIOD_PRESET_HOURS,
  ticketsListCustomCreatedDatesFromParams,
  ticketsListPeriodDateInputStyle,
  ticketsListPeriodSelectValue,
} from '@/lib/tickets-list-period'
import type { ProjectRow, WorkspaceUserRow } from '@/types/api'

const TICKETS_STATUS_FILTER_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'Все' },
  { value: 'new', label: 'Новые' },
  { value: 'open', label: 'Открытые' },
  { value: 'in_progress', label: 'В работе' },
  { value: 'pending', label: 'Ожидание' },
  { value: 'emergency', label: 'Авария' },
  { value: 'resolved', label: 'Решено' },
  { value: 'closed', label: 'Закрыто' },
]

const TICKETS_PRIORITY_FILTER_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'Все' },
  { value: 'low', label: 'Низкий' },
  { value: 'normal', label: 'Обычный' },
  { value: 'high', label: 'Высокий' },
  { value: 'urgent', label: 'Срочный' },
]

const TICKETS_CHANNEL_FILTER_OPTIONS: { value: string; label: string }[] = [
  { value: 'telegram', label: 'Telegram' },
  { value: 'email', label: 'Email' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'widget', label: 'Виджет' },
  { value: 'webchat', label: 'Webchat' },
  { value: 'connector', label: 'Коннектор' },
  { value: 'unknown', label: 'Неопределен' },
]

const QUICK_SORT_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'По умолчанию' },
  { value: 'priority_desc', label: 'Приоритет: срочные сверху' },
  { value: 'priority_asc', label: 'Приоритет: низкие сверху' },
]

function operatorLabel(u: WorkspaceUserRow): string {
  const parts = [u.first_name, u.last_name].filter(Boolean) as string[]
  if (parts.length) return parts.join(' ')
  return u.email || 'Оператор'
}

/** Параметр `project` в URL: slug или id (как в ссылках сайдбара). */
function resolveProjectFromFilterParam(raw: string, projects: ProjectRow[]): ProjectRow | null {
  const t = raw.trim()
  if (!t) return null
  const byId = projects.find((p) => p.id === t)
  if (byId) return byId
  const exactSlug = projects.find((p) => p.slug === t)
  if (exactSlug) return exactSlug
  const low = t.toLowerCase()
  return projects.find((p) => (p.slug || '').toLowerCase() === low) ?? null
}

function countActiveListFilters(sp: URLSearchParams): number {
  let n = 0
  if (sp.get('search')?.trim()) n++
  if (sp.get('created_within_hours')) n++
  if (sp.get('created_date_from') && sp.get('created_date_to')) n++
  if (sp.get('last_message_not_ai') === 'true') n++
  if (sp.get('last_message_not_system') === 'true') n++
  if (sp.get('last_message_not_me') === 'true') n++
  if (sp.get('status')) n++
  if (sp.get('priority')) n++
  if (sp.get('channel_type')) n++
  if (sp.get('assigned') === 'true' || sp.get('assigned') === 'false') n++
  if (sp.get('assigned_to_id')) n++
  if (sp.get('tag_ids')) n++
  if (sp.get('updated_within_hours')) n++
  if (sp.get('assigned_to_me') === 'true') n++
  if (sp.get('exclude_resolved_closed') === 'false') n++
  if (sp.get('include_emergency') === 'true') n++
  if (sp.get('urgent') === 'true') n++
  if (sp.get('project')?.trim() || sp.get('project_id')?.trim()) n++
  return n
}

type Draft = {
  statuses: string[]
  priority: string
  assigned: string
  assignedToMe: boolean
  assignedToOperatorIds: string[]
  channelTypes: string[]
  tagIds: string[]
  excludeResolvedClosed: boolean
  includeEmergency: boolean
  lastMessageNotAi: boolean
  lastMessageNotSystem: boolean
  lastMessageNotMe: boolean
}

function readDraftFromParams(sp: URLSearchParams): Draft {
  const splitCsv = (raw: string | null): string[] =>
    (raw || '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
  const tagRaw = sp.get('tag_ids')
  const tagIds = tagRaw
    ? tagRaw
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
    : []
  const statusInUrl = (sp.get('status') || '').trim()
  const priorityInUrl = (sp.get('priority') || '').trim()
  return {
    statuses: splitCsv(canonicalStatusQueryValue(statusInUrl) || ''),
    priority: canonicalPriorityQueryValue(priorityInUrl) || '',
    assigned: sp.get('assigned') === 'true' || sp.get('assigned') === 'false' ? sp.get('assigned')! : '',
    assignedToMe: sp.get('assigned_to_me') === 'true',
    assignedToOperatorIds: splitCsv(sp.get('assigned_to_id')),
    channelTypes: splitCsv(sp.get('channel_type')),
    tagIds,
    excludeResolvedClosed: excludeResolvedClosedDraftFromParams(sp),
    includeEmergency: sp.get('include_emergency') === 'true',
    lastMessageNotAi: sp.get('last_message_not_ai') === 'true',
    lastMessageNotSystem: sp.get('last_message_not_system') === 'true',
    lastMessageNotMe: sp.get('last_message_not_me') === 'true',
  }
}

export type TicketsListBulkActionsConfig = {
  count: number
  onReply?: () => void
  onStatus?: () => void
  onReleaseAi?: () => void
  onDeleteSelected?: () => void
  onClear?: () => void
  onReplyLabel?: string
  onStatusLabel?: string
  onReleaseAiLabel?: string
  onDeleteSelectedLabel?: string
  onClearLabel?: string
}

type Props = {
  searchParams: URLSearchParams
  setSearchParams: SetURLSearchParams
  disabled?: boolean
  /** Фоновое обновление списка без скрытия таблицы */
  listRefreshing?: boolean
  onRefresh: () => void
  columnVisibility: TicketsListColumnVisibility
  onColumnVisibleChange: (id: TicketsListColumnId, visible: boolean) => void
  workspaceUsers: WorkspaceUserRow[]
  projects: ProjectRow[]
  /** Массовые действия («Все обращения» и «Мозгобот»): перед «Период» */
  bulkActions?: TicketsListBulkActionsConfig | null
  /** Селектор проекта (`?project=`) — на странице «Мозгобот» */
  showProjectFilter?: boolean
  /** Фильтры по последнему сообщению — только вкладка «Мозгобот» */
  showAiLastMessageFilters?: boolean
  /** Размер страницы списка: синхронизируется с `page_size` в URL */
  pageSize?: TicketsPageSizeOption
  onPageSizeChange?: (size: TicketsPageSizeOption) => void
  totalCount?: number
}

const filterLabelProps = { fz: 10, mb: 2, fw: 500, c: 'dimmed' as const }

const toolbarActionBtnStyles = { root: { height: 28, minHeight: 28 } } as const

export function TicketsListToolbar({
  searchParams,
  setSearchParams,
  disabled,
  listRefreshing = false,
  onRefresh,
  columnVisibility,
  onColumnVisibleChange,
  workspaceUsers,
  projects,
  bulkActions = null,
  showProjectFilter = false,
  showAiLastMessageFilters = false,
  pageSize,
  onPageSizeChange,
  totalCount,
}: Props) {
  const searchInUrl = searchParams.get('search') || ''
  const [searchOpen, setSearchOpen] = useState(() => Boolean(searchInUrl.trim()))
  const [searchDraft, setSearchDraft] = useState(searchInUrl)
  const searchFieldFocusedRef = useRef(false)

  useEffect(() => {
    if (searchFieldFocusedRef.current) return
    setSearchDraft(searchInUrl)
    if (searchInUrl.trim()) setSearchOpen(true)
  }, [searchInUrl])

  const applySearchToUrl = useCallback(() => {
    const trimmed = searchDraft.trim()
    if (trimmed === searchInUrl.trim()) return
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev)
        stripTicketsListPageParam(p)
        if (trimmed) p.set('search', trimmed)
        else p.delete('search')
        return p
      },
      { replace: true },
    )
  }, [searchDraft, searchInUrl, setSearchParams])

  const periodValue = ticketsListPeriodSelectValue(searchParams)
  const [customDateFrom, setCustomDateFrom] = useState(() =>
    ticketsListCustomCreatedDatesFromParams(searchParams).from,
  )
  const [customDateTo, setCustomDateTo] = useState(() =>
    ticketsListCustomCreatedDatesFromParams(searchParams).to,
  )

  useEffect(() => {
    const { from, to } = ticketsListCustomCreatedDatesFromParams(searchParams)
    setCustomDateFrom(from)
    setCustomDateTo(to)
  }, [searchParams])

  const applyCustomCreatedDates = useCallback(
    (from: string, to: string) => {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(from) || !/^\d{4}-\d{2}-\d{2}$/.test(to)) return
      if (from > to) return
      setSearchParams(
        (prev) => {
          const p = new URLSearchParams(prev)
          stripTicketsListPageParam(p)
          p.set('created_period', 'custom')
          p.set('created_date_from', from)
          p.set('created_date_to', to)
          p.delete('created_within_hours')
          return p
        },
        { replace: true },
      )
    },
    [setSearchParams],
  )

  const onPeriodChange = (v: string) => {
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev)
        stripTicketsListPageParam(p)
        p.delete('created_date_from')
        p.delete('created_date_to')
        if (v === 'all') {
          p.delete('created_period')
          p.delete('created_within_hours')
        } else if (v === 'custom') {
          p.set('created_period', 'custom')
          p.delete('created_within_hours')
          const { from, to } = ticketsListCustomCreatedDatesFromParams(p)
          p.set('created_date_from', from)
          p.set('created_date_to', to)
        } else {
          p.set('created_period', v)
          const h = TICKETS_LIST_PERIOD_PRESET_HOURS[v]
          if (h) p.set('created_within_hours', String(h))
          else p.delete('created_within_hours')
        }
        return p
      },
      { replace: true },
    )
  }

  const onCustomDateBlur = () => {
    if (periodValue !== 'custom') return
    applyCustomCreatedDates(customDateFrom, customDateTo)
  }

  const [filtersOpen, setFiltersOpen] = useState(false)
  const [draft, setDraft] = useState<Draft>(() => readDraftFromParams(searchParams))
  const filtersPanelWasOpenRef = useRef(false)

  useLayoutEffect(() => {
    if (filtersOpen && !filtersPanelWasOpenRef.current) {
      setDraft(readDraftFromParams(searchParams))
    }
    filtersPanelWasOpenRef.current = filtersOpen
  }, [filtersOpen, searchParams])

  const projectFilterRaw = useMemo(() => (searchParams.get('project') || '').trim(), [searchParams])
  const projectIdsRaw = useMemo(
    () =>
      (searchParams.get('project_id') || '')
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean),
    [searchParams],
  )

  const scopedProject = useMemo(
    () => resolveProjectFromFilterParam(projectFilterRaw, projects),
    [projectFilterRaw, projects],
  )

  const projectSelectData = useMemo(() => {
    const sorted = [...projects].sort((a, b) =>
      (a.name || a.slug || '').localeCompare(b.name || b.slug || '', 'ru'),
    )
    return sorted.map((p) => ({
      value: p.id,
      label: p.name || p.slug || p.id,
    }))
  }, [projects])

  const projectSelectValuesForUi = useMemo(() => {
    if (projectIdsRaw.length) {
      // Backward compatibility: old URLs could contain slugs in project_id.
      return projectIdsRaw
        .map((raw) => {
          const byId = projects.find((p) => p.id === raw)
          if (byId) return byId.id
          const bySlug = projects.find((p) => (p.slug || '').trim() === raw)
          return bySlug ? bySlug.id : raw
        })
        .filter(Boolean)
    }
    if (!projectFilterRaw) return []
    return [scopedProject ? scopedProject.id : projectFilterRaw]
  }, [projectIdsRaw, projectFilterRaw, scopedProject, projects])

  const projectSelectDataResolved = useMemo(() => {
    const missing = projectSelectValuesForUi.filter((v) => !projectSelectData.some((o) => o.value === v))
    if (!missing.length) return projectSelectData
    return [...projectSelectData, ...missing.map((v) => ({ value: v, label: v }))]
  }, [projectSelectData, projectSelectValuesForUi])

  const tagOptions = useMemo(() => {
    if (!projects.length) return []
    if (projectFilterRaw && !scopedProject) return []
    if (scopedProject) {
      const list = scopedProject.tags ?? []
      const opts: { value: string; label: string }[] = []
      for (const t of list) {
        if (t.is_active === false) continue
        opts.push({ value: t.id, label: t.name })
      }
      opts.sort((a, b) => a.label.localeCompare(b.label, 'ru'))
      return [{ value: `__untagged__:${scopedProject.id}`, label: 'Без меток' }, ...opts]
    }
    const opts: { value: string; label: string }[] = []
    for (const pr of projects) {
      const pname = pr.name || pr.slug || 'Проект'
      opts.push({ value: `__untagged__:${pr.id}`, label: `${pname}: Без меток` })
      const list = pr.tags ?? []
      for (const t of list) {
        if (t.is_active === false) continue
        opts.push({ value: t.id, label: `${pname}: ${t.name}` })
      }
    }
    opts.sort((a, b) => a.label.localeCompare(b.label, 'ru'))
    return opts
  }, [projects, scopedProject, projectFilterRaw])

  const operatorSelectData = useMemo(
    () =>
      workspaceUsers
        .filter((u) => canAssignTicketAsWorkspaceUser(u.role))
        .map((u) => ({ value: u.id, label: operatorLabel(u) }))
        .sort((a, b) => a.label.localeCompare(b.label, 'ru')),
    [workspaceUsers],
  )

  /** Значение из URL/черновика до загрузки списка операторов */
  const operatorSelectDataResolved = useMemo(() => {
    const missing = draft.assignedToOperatorIds
      .map((x) => x.trim())
      .filter(Boolean)
      .filter((id) => !operatorSelectData.some((o) => o.value === id))
    if (!missing.length) return operatorSelectData
    return [...operatorSelectData, ...missing.map((id) => ({ value: id, label: `Оператор (${id.slice(0, 8)}…)` }))]
  }, [draft.assignedToOperatorIds, operatorSelectData])

  const renderOperatorOption = useCallback(
    ({ option }: { option: { value: string; label: string; disabled?: boolean } }) => (
      <Group gap={6} wrap="nowrap" align="center" py={2} w="100%">
        <ShiftPresenceDot userId={option.value} size={7} />
        <Text size="xs" truncate style={{ flex: 1, minWidth: 0 }}>
          {option.label}
        </Text>
      </Group>
    ),
    [],
  )

  const tagIdsSafe = useMemo(
    () => draft.tagIds.filter((x): x is string => typeof x === 'string' && x.length > 0),
    [draft.tagIds],
  )

  const tagOptionsResolved = useMemo(() => {
    const missing = tagIdsSafe.filter((id) => !tagOptions.some((o) => o.value === id))
    if (!missing.length) return tagOptions
    return [
      ...tagOptions,
      ...missing.map((id) => ({ value: id, label: `Метка (${id.slice(0, 8)}…)` })),
    ]
  }, [tagOptions, tagIdsSafe])

  const channelSelectData = useMemo(
    () => TICKETS_CHANNEL_FILTER_OPTIONS.map((x) => ({ value: x.value, label: x.label })),
    [],
  )

  const statusSelectData = useMemo(
    () => TICKETS_STATUS_FILTER_OPTIONS.filter((o) => o.value).map((o) => ({ value: o.value, label: o.label })),
    [],
  )

  const priorityNativeData = useMemo(() => {
    const s = draft.priority
    const base = TICKETS_PRIORITY_FILTER_OPTIONS
    if (!s) return base
    if (base.some((o) => o.value === s)) return base
    const label = s.length > 40 ? `${s.slice(0, 40)}…` : s
    return [...base, { value: s, label }]
  }, [draft.priority])

  const openOrToggleFilters = useCallback(() => {
    setFiltersOpen((w) => !w)
  }, [])

  const applyFilters = () => {
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev)
        stripTicketsListPageParam(p)
        const statuses = draft.statuses
          .map((s) => canonicalStatusQueryValue(String(s).trim()))
          .filter(Boolean)
        if (statuses.length) p.set('status', statuses.join(','))
        else p.delete('status')
        const pr = draft.priority.trim()
        if (pr) p.set('priority', canonicalPriorityQueryValue(pr))
        else p.delete('priority')
        if (draft.assigned === 'true' || draft.assigned === 'false') p.set('assigned', draft.assigned)
        else p.delete('assigned')
        if (draft.assignedToMe) {
          p.set('assigned_to_me', 'true')
          p.delete('assigned_to_id')
          p.delete('assigned')
        } else {
          p.delete('assigned_to_me')
          const operatorIds = draft.assignedToOperatorIds.map((x) => x.trim()).filter(Boolean)
          if (operatorIds.length) p.set('assigned_to_id', operatorIds.join(','))
          else p.delete('assigned_to_id')
        }
        if (draft.tagIds.length) p.set('tag_ids', draft.tagIds.join(','))
        else p.delete('tag_ids')
        if (draft.channelTypes.length) p.set('channel_type', draft.channelTypes.join(','))
        else p.delete('channel_type')
        if (draft.excludeResolvedClosed) p.delete('exclude_resolved_closed')
        else p.set('exclude_resolved_closed', 'false')
        if (statuses.includes('emergency')) p.delete('include_emergency')
        else if (draft.includeEmergency) p.set('include_emergency', 'true')
        else p.delete('include_emergency')
        if (showAiLastMessageFilters) {
          if (draft.lastMessageNotAi) p.set('last_message_not_ai', 'true')
          else p.delete('last_message_not_ai')
          if (draft.lastMessageNotSystem) p.set('last_message_not_system', 'true')
          else p.delete('last_message_not_system')
        }
        if (draft.lastMessageNotMe) p.set('last_message_not_me', 'true')
        else p.delete('last_message_not_me')
        return p
      },
      { replace: true },
    )
    setFiltersOpen(false)
  }

  const resetListFilters = () => {
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev)
        const keep = ['sort_by', 'sort_order', 'project', 'project_id']
        const next = new URLSearchParams()
        for (const key of keep) {
          const v = p.get(key)
          if (v != null && v !== '') next.set(key, v)
        }
        return next
      },
      { replace: true },
    )
    setSearchDraft('')
    setSearchOpen(false)
    setFiltersOpen(false)
  }

  const filterCount = useMemo(() => countActiveListFilters(searchParams), [searchParams])
  const showReset = filterCount > 0

  const quickSortValue = useMemo(() => {
    const sortBy = (searchParams.get('sort_by') || '').trim() as TicketsSortByKey | ''
    const sortOrder = searchParams.get('sort_order') === 'asc' ? 'asc' : 'desc'
    if (sortBy === 'priority') return sortOrder === 'asc' ? 'priority_asc' : 'priority_desc'
    return ''
  }, [searchParams])

  const onQuickSortChange = (value: string) => {
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev)
        stripTicketsListPageParam(p)
        if (value === 'priority_desc') {
          p.set('sort_by', 'priority')
          p.set('sort_order', 'desc')
        } else if (value === 'priority_asc') {
          p.set('sort_by', 'priority')
          p.set('sort_order', 'asc')
        } else {
          p.delete('sort_by')
          p.delete('sort_order')
        }
        return p
      },
      { replace: true },
    )
  }

  const filterFieldStyles = {
    input: { minHeight: 28, fontSize: 'var(--mantine-font-size-xs)' },
  } as const

  return (
    <Box style={{ width: '100%' }}>
      <Group justify="space-between" align="center" wrap="wrap" gap="sm">
        <Group gap="xs" wrap="wrap" align="center" style={{ flex: 1, minWidth: 0 }}>
          <Group gap={6} wrap="nowrap" align="center">
            <ActionIcon
              variant="default"
              size="md"
              aria-label="Поиск"
              title="Поиск"
              disabled={disabled}
              onClick={() => setSearchOpen((o) => !o)}
            >
              <IconSearch size={18} stroke={1.5} />
            </ActionIcon>
            {searchOpen ? (
              <Group gap={6} wrap="nowrap" align="center" style={{ flex: 1, minWidth: 0 }}>
                <TextInput
                  placeholder="Поиск по обращениям…"
                  value={searchDraft}
                  onChange={(e) => setSearchDraft(e.currentTarget.value)}
                  onFocus={() => {
                    searchFieldFocusedRef.current = true
                  }}
                  onBlur={() => {
                    searchFieldFocusedRef.current = false
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      applySearchToUrl()
                    }
                  }}
                  disabled={disabled}
                  size="xs"
                  style={{ flex: '1 1 160px', minWidth: 0 }}
                  maxLength={500}
                  rightSection={
                    searchDraft ? (
                      <ActionIcon
                        size="sm"
                        variant="transparent"
                        aria-label="Очистить поиск"
                        onClick={() => {
                          setSearchDraft('')
                          setSearchParams(
                            (prev) => {
                              const p = new URLSearchParams(prev)
                              stripTicketsListPageParam(p)
                              p.delete('search')
                              return p
                            },
                            { replace: true },
                          )
                        }}
                      >
                        <IconX size={14} />
                      </ActionIcon>
                    ) : null
                  }
                />
                <Button
                  size="xs"
                  h={28}
                  px="sm"
                  disabled={disabled || searchDraft.trim() === searchInUrl.trim()}
                  onClick={applySearchToUrl}
                >
                  Найти
                </Button>
              </Group>
            ) : null}
          </Group>
          {showProjectFilter ? (
            <Group gap={6} wrap="nowrap" align="center">
              <Text size="xs" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
                Проект:
              </Text>
              <MultiSelect
                size="xs"
                searchable
                clearable
                placeholder={projects.length === 0 ? 'Загрузка…' : 'Все'}
                disabled={disabled || projects.length === 0}
                value={projectSelectValuesForUi}
                onChange={(values) => {
                  setSearchParams(
                    (prev) => {
                      const p = new URLSearchParams(prev)
                      stripTicketsListPageParam(p)
                      const vals = values.map((x) => x.trim()).filter(Boolean)
                      p.delete('project')
                      p.delete('project_id')
                      if (vals.length > 0) p.set('project_id', vals.join(','))
                      return p
                    },
                    { replace: true },
                  )
                }}
                data={projectSelectDataResolved}
                styles={{ input: { minWidth: 160, maxWidth: 280 } }}
                maxDropdownHeight={260}
                nothingFoundMessage="Нет"
              />
            </Group>
          ) : null}
          {typeof totalCount === 'number' ? (
            <Text size="xs" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
              Всего: <Text component="span" fw={600}>{totalCount}</Text>
            </Text>
          ) : null}
        </Group>

        <Group gap={6} wrap="wrap" justify="flex-end" align="center">
          {bulkActions && bulkActions.count > 0 ? (
            <Group gap={6} wrap="wrap" align="center">
              <Text size="xs" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
                Выбрано: {bulkActions.count}
              </Text>
              {bulkActions.onReply ? (
                <ActionButton
                  type="button"
                  action="apply"
                  variant="default"
                  color="gray"
                  size="xs"
                  styles={toolbarActionBtnStyles}
                  label={`${bulkActions.onReplyLabel ?? 'Массово ответить'} (${bulkActions.count})`}
                  leftSection={<IconMessage size={16} stroke={1.5} />}
                  disabled={disabled}
                  onClick={bulkActions.onReply}
                />
              ) : null}
              {bulkActions.onStatus ? (
                <ActionButton
                  type="button"
                  action="apply"
                  variant="default"
                  color="gray"
                  size="xs"
                  styles={toolbarActionBtnStyles}
                  label={`${bulkActions.onStatusLabel ?? 'Изменить статус'} (${bulkActions.count})`}
                  leftSection={<IconEdit size={16} stroke={1.5} />}
                  disabled={disabled}
                  onClick={bulkActions.onStatus}
                />
              ) : null}
              {bulkActions.onReleaseAi ? (
                <ActionButton
                  type="button"
                  action="apply"
                  variant="default"
                  color="gray"
                  size="xs"
                  styles={toolbarActionBtnStyles}
                  label={`${bulkActions.onReleaseAiLabel ?? 'Снять с Мозгобота'} (${bulkActions.count})`}
                  leftSection={<IconRobot size={16} stroke={1.5} />}
                  disabled={disabled}
                  onClick={bulkActions.onReleaseAi}
                />
              ) : null}
              {bulkActions.onDeleteSelected ? (
                <ActionButton
                  type="button"
                  action="delete"
                  variant="default"
                  color="red"
                  size="xs"
                  styles={toolbarActionBtnStyles}
                  label={`${bulkActions.onDeleteSelectedLabel ?? 'Удалить выбранные'} (${bulkActions.count})`}
                  disabled={disabled}
                  onClick={bulkActions.onDeleteSelected}
                />
              ) : null}
              {bulkActions.onClear ? (
                <ActionButton
                  type="button"
                  action="cancel"
                  variant="default"
                  color="gray"
                  size="xs"
                  styles={toolbarActionBtnStyles}
                  label={bulkActions.onClearLabel ?? 'Снять выделение'}
                  leftSection={<IconX size={16} stroke={1.5} />}
                  disabled={disabled}
                  onClick={bulkActions.onClear}
                />
              ) : null}
            </Group>
          ) : null}
          {pageSize != null && onPageSizeChange ? (
            <Group gap={6} wrap="nowrap" align="center">
              <Text size="xs" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
                На странице:
              </Text>
              <NativeSelect
                size="xs"
                disabled={disabled}
                value={String(pageSize)}
                onChange={(e) => {
                  const n = parseInt(e.currentTarget.value, 10)
                  if (isTicketsPageSizeOption(n)) onPageSizeChange(n)
                }}
                data={TICKETS_PAGE_SIZE_OPTIONS.map((n) => ({
                  value: String(n),
                  label: String(n),
                }))}
                styles={{ input: { minWidth: 56 } }}
              />
            </Group>
          ) : null}
          <Group gap={6} wrap="nowrap" align="center">
            <Text size="xs" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
              Сортировка:
            </Text>
            <NativeSelect
              size="xs"
              disabled={disabled}
              value={quickSortValue}
              onChange={(e) => onQuickSortChange(e.currentTarget.value)}
              data={QUICK_SORT_OPTIONS}
              styles={{ input: { minWidth: 178 } }}
            />
          </Group>
          <Group gap={6} wrap="nowrap" align="center">
            <Text size="xs" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
              Период:
            </Text>
            <NativeSelect
              size="xs"
              disabled={disabled}
              value={periodValue}
              onChange={(e) => onPeriodChange(e.currentTarget.value)}
              data={[
                { value: '1', label: '1 день' },
                { value: '7', label: '7 дней' },
                { value: '14', label: '14 дней' },
                { value: '30', label: '30 дней' },
                { value: 'all', label: 'Все' },
                { value: 'custom', label: 'Своё' },
              ]}
              styles={{ input: { minWidth: 96 } }}
            />
            {periodValue === 'custom' ? (
              <>
                <Group gap={4} wrap="nowrap" align="center">
                  <Text size="xs" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
                    От
                  </Text>
                  <input
                    type="date"
                    aria-label="Дата создания от"
                    disabled={disabled}
                    value={customDateFrom}
                    onChange={(e) => setCustomDateFrom(e.currentTarget.value)}
                    onBlur={onCustomDateBlur}
                    style={ticketsListPeriodDateInputStyle}
                  />
                </Group>
                <Group gap={4} wrap="nowrap" align="center">
                  <Text size="xs" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
                    До
                  </Text>
                  <input
                    type="date"
                    aria-label="Дата создания до"
                    disabled={disabled}
                    value={customDateTo}
                    onChange={(e) => setCustomDateTo(e.currentTarget.value)}
                    onBlur={onCustomDateBlur}
                    style={ticketsListPeriodDateInputStyle}
                  />
                </Group>
              </>
            ) : null}
          </Group>
          <Box pos="relative" style={{ display: 'inline-block' }}>
            <ActionIcon
              variant="default"
              size="md"
              aria-label="Фильтры"
              title="Фильтры"
              disabled={disabled}
              onClick={openOrToggleFilters}
            >
              <IconFilter size={18} stroke={1.5} />
            </ActionIcon>
            {filterCount > 0 ? (
              <Badge
                size="xs"
                circle
                variant="filled"
                color="blue"
                style={{
                  position: 'absolute',
                  top: -4,
                  right: -4,
                  minWidth: 18,
                  height: 18,
                  padding: 0,
                  fontSize: 10,
                }}
              >
                {filterCount > 9 ? '9+' : filterCount}
              </Badge>
            ) : null}
          </Box>
          {showReset ? (
            <ActionIcon
              variant="default"
              size="md"
              aria-label="Сбросить фильтры"
              title="Сбросить фильтры"
              disabled={disabled}
              onClick={resetListFilters}
            >
              <IconRotateClockwise size={18} stroke={1.5} />
            </ActionIcon>
          ) : null}
          <Popover position="bottom-end" shadow="md" withArrow>
            <Popover.Target>
              <ActionIcon
                variant="default"
                size="md"
                aria-label="Колонки"
                title="Видимость колонок"
                disabled={disabled}
              >
                <IconLayoutColumns size={18} stroke={1.5} />
              </ActionIcon>
            </Popover.Target>
            <Popover.Dropdown p="sm">
              <Text size="xs" fw={600} mb="xs" c="dimmed">
                Видимость колонок
              </Text>
              <StackMenu gap="xs">
                {TICKETS_LIST_COLUMNS_MENU_ORDER.map((id) => (
                  <Checkbox
                    key={id}
                    size="xs"
                    label={TICKETS_LIST_COLUMN_LABEL[id]}
                    checked={columnVisibility[id]}
                    onChange={() => onColumnVisibleChange(id, !columnVisibility[id])}
                  />
                ))}
              </StackMenu>
            </Popover.Dropdown>
          </Popover>
          <ActionIcon
            variant="default"
            size="md"
            aria-label="Обновить список"
            title={listRefreshing ? 'Обновление…' : 'Обновить список'}
            disabled={disabled || listRefreshing}
            onClick={onRefresh}
          >
            {listRefreshing ? <Loader size={16} type="oval" /> : <IconRefresh size={18} stroke={1.5} />}
          </ActionIcon>
        </Group>
      </Group>

      <Collapse in={filtersOpen}>
        <Box
          mt={6}
          px="xs"
          py={6}
          style={{
            borderTop: '1px solid light-dark(var(--mantine-color-gray-3), var(--mantine-color-dark-4))',
            background: 'light-dark(var(--mantine-color-gray-0), var(--mantine-color-dark-6))',
            borderRadius: 'var(--mantine-radius-sm)',
          }}
        >
          <Group wrap="wrap" align="flex-end" gap={8} justify="space-between">
            <Group wrap="wrap" align="flex-end" gap={8} style={{ flex: 1, minWidth: 0 }}>
              <MultiSelect
                size="xs"
                label="Статус"
                labelProps={filterLabelProps}
                placeholder="Все"
                value={draft.statuses}
                onChange={(values) =>
                  setDraft((d) => ({
                    ...d,
                    statuses: values.map((x) => String(x).trim()).filter(Boolean),
                    ...(values.includes('emergency') ? { includeEmergency: false } : {}),
                  }))
                }
                data={statusSelectData}
                styles={filterFieldStyles}
                w={220}
                clearable
                searchable
                nothingFoundMessage="Нет"
              />
              <NativeSelect
                size="xs"
                label="Приоритет"
                labelProps={filterLabelProps}
                value={draft.priority}
                onChange={(e) => setDraft((d) => ({ ...d, priority: e.currentTarget.value }))}
                data={priorityNativeData}
                styles={filterFieldStyles}
                w={118}
              />
              <NativeSelect
                size="xs"
                label="Назначение"
                labelProps={filterLabelProps}
                disabled={disabled || draft.assignedToMe}
                value={draft.assigned}
                onChange={(e) => setDraft((d) => ({ ...d, assigned: e.currentTarget.value }))}
                data={[
                  { value: '', label: 'Все' },
                  { value: 'true', label: 'Назначенные' },
                  { value: 'false', label: 'Не назначенные' },
                ]}
                styles={filterFieldStyles}
                w={132}
              />
              <MultiSelect
                size="xs"
                label="Оператор"
                labelProps={filterLabelProps}
                placeholder="Любой"
                searchable
                clearable
                disabled={disabled || draft.assignedToMe}
                data={operatorSelectDataResolved}
                value={draft.assignedToOperatorIds}
                onChange={(vals) =>
                  setDraft((d) => ({
                    ...d,
                    assignedToOperatorIds: vals.map((x) => String(x).trim()).filter(Boolean),
                    assignedToMe: false,
                  }))
                }
                renderOption={renderOperatorOption}
                styles={filterFieldStyles}
                w={240}
                maxDropdownHeight={220}
                nothingFoundMessage="Нет"
              />
              <MultiSelect
                size="xs"
                label="Канал"
                labelProps={filterLabelProps}
                placeholder="Все"
                data={channelSelectData}
                value={draft.channelTypes}
                onChange={(vals) =>
                  setDraft((d) => ({
                    ...d,
                    channelTypes: vals.map((x) => String(x).trim()).filter(Boolean),
                  }))
                }
                styles={filterFieldStyles}
                w={180}
                clearable
                searchable
                nothingFoundMessage="Нет"
              />
              <MultiSelect
                size="xs"
                label="Метки"
                labelProps={filterLabelProps}
                placeholder={tagOptionsResolved.length ? 'Выберите' : 'Нет меток'}
                disabled={disabled || tagOptionsResolved.length === 0}
                data={tagOptionsResolved}
                value={tagIdsSafe}
                onChange={(ids) =>
                  setDraft((d) => ({
                    ...d,
                    tagIds: ids.filter((x): x is string => typeof x === 'string'),
                  }))
                }
                styles={filterFieldStyles}
                w={260}
                maxDropdownHeight={220}
                searchable
                clearable
                nothingFoundMessage="Нет"
              />
              <Group gap="sm" wrap="wrap" align="center" pb={2}>
                <Checkbox
                  size="xs"
                  label="Скрыть обращения других операторов"
                  title="Только назначенные на меня или активно перераспределённые на меня"
                  checked={draft.assignedToMe}
                  onChange={() =>
                    setDraft((d) => {
                      const next = !d.assignedToMe
                      return {
                        ...d,
                        assignedToMe: next,
                        assigned: next ? '' : d.assigned,
                        assignedToOperatorIds: next ? [] : d.assignedToOperatorIds,
                      }
                    })
                  }
                />
                <Checkbox
                  size="xs"
                  label="Скрыть обращения, где мой последний ответ"
                  title="Скрывает обращения, где последнее сообщение в диалоге — мой ответ клиенту. Заметки и системные события не учитываются."
                  checked={draft.lastMessageNotMe}
                  onChange={() => setDraft((d) => ({ ...d, lastMessageNotMe: !d.lastMessageNotMe }))}
                />
                <Checkbox
                  size="xs"
                  label="Без реш./закр."
                  title="Кроме решённых и закрытых"
                  checked={draft.excludeResolvedClosed}
                  onChange={() =>
                    setDraft((d) => ({ ...d, excludeResolvedClosed: !d.excludeResolvedClosed }))
                  }
                />
                <Checkbox
                  size="xs"
                  label="Аварии"
                  title="Включить аварийные"
                  checked={draft.includeEmergency}
                  disabled={draft.statuses.includes('emergency')}
                  onChange={() =>
                    setDraft((d) => ({ ...d, includeEmergency: !d.includeEmergency }))
                  }
                />
                {showAiLastMessageFilters ? (
                  <>
                    <Checkbox
                      size="xs"
                      label="Не от Мозгобота"
                      title="Последнее сообщение не от Мозгобота (клиент, оператор или другое)"
                      checked={draft.lastMessageNotAi}
                      onChange={() =>
                        setDraft((d) => ({ ...d, lastMessageNotAi: !d.lastMessageNotAi }))
                      }
                    />
                    <Checkbox
                      size="xs"
                      label="Не системное"
                      title="Последнее сообщение не системное"
                      checked={draft.lastMessageNotSystem}
                      onChange={() =>
                        setDraft((d) => ({
                          ...d,
                          lastMessageNotSystem: !d.lastMessageNotSystem,
                        }))
                      }
                    />
                  </>
                ) : null}
              </Group>
            </Group>

            <Group gap={6} wrap="nowrap" align="center" pb={2} style={{ flexShrink: 0 }}>
              <Button size="xs" h={28} px="sm" onClick={applyFilters}>
                Применить
              </Button>
              <Button
                size="xs"
                h={28}
                px="sm"
                variant="default"
                onClick={() => setDraft(readDraftFromParams(searchParams))}
              >
                Отменить
              </Button>
              <ActionIcon
                variant="subtle"
                size="md"
                aria-label="Закрыть панель фильтров"
                title="Закрыть"
                onClick={() => setFiltersOpen(false)}
              >
                <IconX size={18} stroke={1.5} />
              </ActionIcon>
            </Group>
          </Group>
        </Box>
      </Collapse>
    </Box>
  )
}
